import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ROLES, type Role } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { authStore, useAuth } from "../../auth/store";
import { ROLE_DETAILS, ROLE_LABELS, ROLE_SUMMARY, useCan } from "../../auth/roles";
import { OrgLayout } from "../../components/Shell";
import { ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, useToast } from "../../components/ui";
import { ErrorState } from "../../components/States";
import { formatDateTime, formatRelative } from "../../lib/format";


/** Every role side by side: what it can do, what it cannot. */
export function RoleGuide({ current }: { current?: Role }) {
  return (
    <div className="role-guide" data-testid="role-guide">
      {ROLES.map((r) => (
        <section key={r} className={`role-guide-item${r === current ? " current" : ""}`} aria-labelledby={`role-${r}`}>
          <h3 id={`role-${r}`} style={{ margin: "0 0 2px", fontSize: 14 }}>
            {ROLE_LABELS[r]}
            {r === current && <span className="badge neutral" style={{ marginLeft: 8 }}>your role</span>}
          </h3>
          <p className="muted small" style={{ margin: "0 0 8px" }}>{ROLE_SUMMARY[r]}</p>
          <div className="role-guide-cols">
            <div>
              <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>Can</div>
              <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                {ROLE_DETAILS[r].can.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
            {ROLE_DETAILS[r].cannot.length > 0 && (
              <div>
                <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>Cannot</div>
                <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
                  {ROLE_DETAILS[r].cannot.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

export function MembersPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const auth = useAuth();
  const can = useCan();
  const members = useQuery({ queryKey: queryKeys.members(orgId), queryFn: () => api.orgs.members(orgId) });
  // Invitations are readable from developer up; asking as read-only would only produce a 403.
  const invites = useQuery({ queryKey: queryKeys.invitations(orgId), queryFn: () => api.orgs.invitations(orgId), enabled: can.develop });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("developer");
  const [removing, setRemoving] = useState<{ userId: string; email: string } | null>(null);
  const [revokingInvite, setRevokingInvite] = useState<{ id: string; email: string } | null>(null);
  const [renaming, setRenaming] = useState<{ userId: string; email: string; name: string } | null>(null);

  const rename = useMutation({
    mutationFn: ({ userId, name }: { userId: string; name: string }) => api.orgs.renameMember(orgId, userId, name.trim()),
    onSuccess: (_, v) => {
      setRenaming(null);
      void qc.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      // Renaming yourself also changes the name in the top bar.
      if (v.userId === auth.me?.user.id) void authStore.load();
      toast.push("success", "Name updated.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const setRoleMut = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) => api.orgs.setRole(orgId, userId, role),
    onSuccess: (_, v) => {
      void qc.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      toast.push("success", `Role updated to ${ROLE_LABELS[v.role]}.`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.orgs.removeMember(orgId, userId),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: queryKeys.members(orgId) });
      toast.push("info", "Member removed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const invite = useMutation({
    mutationFn: () => api.orgs.invite(orgId, { email, role }),
    onSuccess: () => {
      setInviteOpen(false);
      setEmail("");
      void qc.invalidateQueries({ queryKey: queryKeys.invitations(orgId) });
      toast.push("success", `Invitation sent to ${email}.`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.orgs.revokeInvite(orgId, id),
    onSuccess: () => {
      setRevokingInvite(null);
      void qc.invalidateQueries({ queryKey: queryKeys.invitations(orgId) });
      toast.push("info", "Invitation revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const resend = useMutation({
    mutationFn: async (inv: { id: string; email: string; role: Role }) => {
      await api.orgs.revokeInvite(orgId, inv.id);
      return api.orgs.invite(orgId, { email: inv.email, role: inv.role });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invitations(orgId) });
      toast.push("success", "Invitation re-sent.");
    },
  });

  const owners = (members.data ?? []).filter((m) => m.role === "owner").length;
  const myId = auth.me?.user.id;
  // Only an owner may hand out the owner role; the API refuses it from an admin.
  const grantable = can.owner ? ROLES : ROLES.filter((r) => r !== "owner");

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader
        title="Members"
        actions={
          <>
            <button className="btn ghost" onClick={() => setGuideOpen(true)} data-testid="role-guide-open">
              What can each role do?
            </button>
            {can.members && (
              <button className="btn primary" onClick={() => setInviteOpen(true)}>
                Invite member
              </button>
            )}
          </>
        }
      />
      <div className="card stack-table">
        {members.isError ? (
          <ErrorState error={members.error} retry={() => void members.refetch()} />
        ) : (
        <DataTable
          ariaLabel="Members"
          rows={members.data ?? []}
          loading={members.isPending}
          rowKey={(m) => m.userId}
          columns={[
            {
              key: "who",
              header: "Member",
              label: "Member",
              render: (m) => (
                <span className="member-who">
                  <span>
                    <strong>{m.name || m.email}</strong>
                    {m.name && <div className="muted small">{m.email}</div>}
                  </span>
                  {can.members && m.source !== "scim" && (
                    <button type="button" className="btn ghost sm member-rename" aria-label={`Edit name for ${m.email}`} title="Edit name" onClick={() => setRenaming({ userId: m.userId, email: m.email, name: m.name ?? "" })}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                    </button>
                  )}
                </span>
              ),
            },
            {
              key: "role",
              header: "Role",
              label: "Role",
              render: (m) =>
                can.members ? (
                  <select
                    className="input"
                    aria-label={`Role for ${m.email}`}
                    value={m.role}
                    title={ROLE_SUMMARY[m.role]}
                    disabled={m.userId === myId || (m.role === "owner" && owners === 1) || m.source === "scim" || (m.role === "owner" && !can.owner)}
                    onChange={(e) => setRoleMut.mutate({ userId: m.userId, role: e.target.value as Role })}
                    style={{ minWidth: 150 }}
                    autoComplete="off"
                  >
                    {(m.role === "owner" ? ROLES : grantable).map((r) => (
                      <option key={r} value={r} title={ROLE_SUMMARY[r]}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span title={ROLE_SUMMARY[m.role]}>{ROLE_LABELS[m.role]}</span>
                ),
            },
            { key: "mfa", header: "2FA", label: "2FA", render: (m) => (m.mfaEnabled ? <span className="badge ok">on</span> : <span className="badge warn">off</span>) },
            { key: "src", header: "Source", label: "Source", render: (m) => <span className="badge neutral">{m.source}</span> },
            { key: "last", header: "Last login", label: "Last login", render: (m) => (m.lastLoginAt ? formatRelative(m.lastLoginAt) : <span className="muted">never</span>) },
            ...(can.members
              ? [
                  {
                    key: "act",
                    header: "",
                    align: "right" as const,
                    render: (m: { userId: string; email: string; role: Role; source: string }) => {
                      const locked = (m.role === "owner" && owners === 1) || (m.role === "owner" && !can.owner);
                      if (m.userId === myId || m.source === "scim" || locked) return null;
                      return (
                        <RowMenu label={`Actions for ${m.email}`}>
                          <button type="button" role="menuitem" className="danger" onClick={() => setRemoving({ userId: m.userId, email: m.email })}>
                            Remove
                          </button>
                        </RowMenu>
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
        )}
      </div>

      {can.develop && (
        <>
          <h2 style={{ fontSize: 14, margin: "24px 0 8px" }}>Pending invitations</h2>
          <div className="card stack-table">
            {invites.isError ? (
              <ErrorState error={invites.error} retry={() => void invites.refetch()} />
            ) : (
            <DataTable
              ariaLabel="Invitations"
              rows={invites.data ?? []}
              loading={invites.isPending}
              rowKey={(i) => i.id}
              empty="No pending invitations."
              columns={[
                { key: "email", header: "Email", label: "Email", render: (i) => i.email },
                { key: "role", header: "Role", label: "Role", render: (i) => <span title={ROLE_SUMMARY[i.role]}>{ROLE_LABELS[i.role]}</span> },
                { key: "exp", header: "Expires", label: "Expires", render: (i) => formatDateTime(i.expiresAt) },
                ...(can.members
                  ? [
                      {
                        key: "act",
                        header: "",
                        align: "right" as const,
                        render: (i: { id: string; email: string; role: Role }) => (
                          <RowMenu label={`Actions for ${i.email}`}>
                            <button type="button" role="menuitem" onClick={() => resend.mutate(i)} disabled={resend.isPending}>Resend</button>
                            <button type="button" role="menuitem" className="danger" onClick={() => setRevokingInvite({ id: i.id, email: i.email })}>Revoke</button>
                          </RowMenu>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
            )}
          </div>
        </>
      )}

      <Dialog open={guideOpen} onClose={() => setGuideOpen(false)} title="What each role can do" width={960}>
        <RoleGuide current={can.role} />
      </Dialog>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite a member" footer={<><button className="btn" onClick={() => setInviteOpen(false)}>Cancel</button><button className="btn primary" onClick={() => invite.mutate()} disabled={!email.includes("@") || invite.isPending}>Send invitation</button></>}>
        <Field label="Email">{(p) => <input {...p} className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}</Field>
        <Field label="Role" hint={ROLE_SUMMARY[role]}>
          {(p) => (
            <select {...p} className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {grantable.map((r) => (
                <option key={r} value={r} title={ROLE_SUMMARY[r]}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <details className="small" style={{ marginTop: 4 }}>
          <summary className="muted">Compare all roles</summary>
          <RoleGuide current={can.role} />
        </details>
      </Dialog>

      <Dialog
        open={!!renaming}
        onClose={() => setRenaming(null)}
        title="Edit name"
        footer={<><button className="btn" onClick={() => setRenaming(null)}>Cancel</button><button className="btn primary" onClick={() => renaming && rename.mutate({ userId: renaming.userId, name: renaming.name })} disabled={!renaming?.name.trim() || rename.isPending}>Save</button></>}
      >
        <p className="muted small" style={{ marginTop: 0 }}>This is the name your team sees for <strong>{renaming?.email}</strong>, for example on issues assigned to them.</p>
        <Field label="Name">
          {(p) => (
            <input
              {...p}
              className="input"
              value={renaming?.name ?? ""}
              onChange={(e) => setRenaming((r) => (r ? { ...r, name: e.target.value } : r))}
              onKeyDown={(e) => { if (e.key === "Enter" && renaming?.name.trim()) rename.mutate({ userId: renaming.userId, name: renaming.name }); }}
              maxLength={120}
              autoFocus
            />
          )}
        </Field>
      </Dialog>

      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove member"
        confirmLabel="Remove member"
        busy={remove.isPending}
        onClose={() => { if (!remove.isPending) setRemoving(null); }}
        onConfirm={() => removing && remove.mutate(removing.userId)}
      >
        <p style={{ margin: 0 }}><strong>{removing?.email}</strong> loses access immediately.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!revokingInvite}
        danger
        title="Revoke invitation"
        confirmLabel="Revoke invitation"
        busy={revoke.isPending}
        onClose={() => { if (!revoke.isPending) setRevokingInvite(null); }}
        onConfirm={() => revokingInvite && revoke.mutate(revokingInvite.id)}
      >
        <p style={{ margin: 0 }}>The invitation to <strong>{revokingInvite?.email}</strong> can no longer be accepted.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}
